import React from 'react'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { cm } from '~/utils/utils'
import { CreditsCard } from './CreditsCard'
import type { CreditsData, UsageOverviewData } from './types'
import { UsageOverviewCard } from './UsageOverviewCard'

interface UsageStatsCardsProps {
  usageOverview: UsageOverviewData
  credits: CreditsData
  layout?: 'horizontal' | 'compact'
  className?: string
  showLimits?: boolean
  isAllWorkspacesSelected?: boolean
  isBillableFlowPlan?: boolean
  onCreditsDetailsClick?: () => void
  isSuperAdmin?: boolean
  creditsSectionTitle?: string
  billableFlowsTitle?: string
}

export const UsageStatsCards: React.FC<UsageStatsCardsProps> = ({
  usageOverview,
  credits,
  layout = 'horizontal',
  className,
  showLimits = true,
  isAllWorkspacesSelected = true,
  isBillableFlowPlan = false,
  onCreditsDetailsClick,
  isSuperAdmin = false,
  creditsSectionTitle,
  billableFlowsTitle
}) => {
  const isCompactViewport = useCompactViewport()

  // [Usage Overview | Card Stack] row in two shapes:
  //   'compact'    — Usage Overview: flex-1, Card Stack: 320px
  //   'horizontal' — Usage Overview: 380px, Card Stack: flex-1
  //
  // Sizing rule (desktop, mirrored in PersonalUsageDialog):
  //   - Each card inside CreditsCard is fixed at ≈H/2 = 127px
  //   - Usage Overview has min-height H = 269px
  //   - 1 card  → stack = 127, Overview = 269, stack sits at top with room below
  //   - 2 cards → stack = 270 ≈ Overview (1px slack from gap-4)
  //   - 3 cards → stack = 397, Overview stretches via default flex stretch
  //   - 4 cards → stack = 524, Overview stretches further
  // Cards never shrink because their height is fixed; the super-admin
  // footnote inside CreditsCard is sized to fit one line within 127px.
  if (layout === 'compact') {
    return (
      <div className={cm(isCompactViewport ? 'flex flex-col gap-4' : 'flex gap-4', className)}>
        <UsageOverviewCard
          data={usageOverview}
          className={isCompactViewport ? 'w-full h-auto' : 'flex-1 min-h-[269px]'}
        />
        {/* Card stack column. CreditsCard internally renders 1 or 2 sub-cards,
            each at fixed h-[127px] on desktop. */}
        <div className={cm('flex flex-col', isCompactViewport ? 'w-full' : 'w-[320px]')}>
          <CreditsCard
            data={credits}
            className='w-full'
            showLimits={showLimits}
            isAllWorkspacesSelected={isAllWorkspacesSelected}
            isBillableFlowPlan={isBillableFlowPlan}
            onDetailsClick={onCreditsDetailsClick}
            isSuperAdmin={isSuperAdmin}
            creditsSectionTitle={creditsSectionTitle}
            billableFlowsTitle={billableFlowsTitle}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={cm(isCompactViewport ? 'flex flex-col gap-4' : 'flex gap-6', className)}>
      <UsageOverviewCard
        data={usageOverview}
        className={isCompactViewport ? 'w-full h-auto' : 'w-[380px] min-h-[269px]'}
      />
      {/* Card stack column. CreditsCard internally renders 1 or 2 sub-cards,
          each at fixed h-[127px] on desktop. */}
      <div className={cm('flex-1 flex flex-col', isCompactViewport && 'w-full')}>
        <CreditsCard
          data={credits}
          className='w-full'
          showLimits={showLimits}
          isAllWorkspacesSelected={isAllWorkspacesSelected}
          isBillableFlowPlan={isBillableFlowPlan}
          onDetailsClick={onCreditsDetailsClick}
          isSuperAdmin={isSuperAdmin}
          creditsSectionTitle={creditsSectionTitle}
          billableFlowsTitle={billableFlowsTitle}
        />
      </div>
    </div>
  )
}

export default UsageStatsCards
